import { useState, useEffect } from 'react'
import PaymentsRequestsTable from '../../../../components/bankAccount/paymentRequests/PaymentsRequestsTable'
import Box from '@mui/material/Box'
import theme from '../../../../helpers/theme'
import { backendGetRequest } from '../../../../helpers/callBackend'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import HeaderBox from '../../../../components/generic/arpariBoxes/headerBox'
import HideOnMobile from '../../../../components/generic/hideOnMobile'
import Stack from '@mui/material/Stack'
import checkFlag from '../../../../helpers/featureFlags'
import ComingSoonTab from '../sendFundsDwolla/ComingSoonTab'
import OuterBox from '../../../../components/generic/arpariBoxes/OuterBox'
import BulkApprovalButton from '../../../../components/bankAccount/paymentRequests/BulkApprovalButton'
import { Space } from 'antd'
import { useUcs } from '../../../../helpers/contexts/UserCompanySettingsContext'

const Approvals = () => {
  const [paymentRequests, setPaymentRequests] = useState([])
  const [totalRecords, setTotalRecords] = useState(-1)
  const [loading, setLoading] = useState(false)
  const [userCompanySettings, setUserCompanySettings] = useState({})
  const [approvableRequests, setApprovableRequests] = useState([])
  const [displayAllData, setDisplayAllData] = useState(true)
  const [selectedRowIds, setSelectedRowIds] = useState([])

  const ucsContext = useUcs()

  const onRowSelect = (rowIds) => {
    setSelectedRowIds([...rowIds])
  }

  const filters = {
    all: true,
    unapproved: true,
    uncanceled: true,
  }

  const getPrPage = async (pageNum) => {
    try {
      const prResp = await backendGetRequest('/api/payments/payment-request', {
        params: {
          ...filters,
          page_index: pageNum,
        },
      })
      const paymentRequestArray = prResp.data?.data
      setPaymentRequests((prevState) => [...prevState, ...paymentRequestArray])
    } catch (error) {
      // error getting payment request objects
      return
    }
  }

  const getPaymentRequests = async () => {
    setPaymentRequests([])
    setLoading(true)
    let totalPages = 0
    try {
      const paginationResp = await backendGetRequest('/api/payments/payment-request', {
        params: {
          ...filters,
          page_index: 0,
        },
      })
      totalPages = paginationResp.data?.total_pages
      setTotalRecords(paginationResp.data?.total_records)

    } catch (error) {
      // error while getting pagination info
      return
    }

    for (let pageNum = 1; pageNum < totalPages + 1; pageNum++) {
      getPrPage(pageNum)
    }
  }

  const updatePaymentRequests = (paymentRequest, method) => {
    // edit payment requests data displayed to user
    if (!paymentRequest) {
      return
    }
    if (paymentRequest.dwolla_payment_id || paymentRequest.is_canceled) {
      // payment request has been approved or canceled
      let newRequests = []
      for (const request of paymentRequests) {
        if (request.id !== paymentRequest.id) {
          newRequests.push(request)
        }
      }
      setPaymentRequests(newRequests)
    }
  }

  const updateBulkPaymentRequests = () => {
    getPaymentRequests()
  }

  const toggleData = () => {
    setDisplayAllData((prevState) => !prevState)
  }

  const filterApprovableRequests = (requests) => {
    // this is where we might want to start checking other UCS permissions
    let approvableByMe = []
    for (const request of requests) {
      // if user created this PR
      if (request.created_by === userCompanySettings.user_display_name) {
        approvableByMe.push(request)
      }
    }
    return approvableByMe
  }

  useEffect(() => {
    if (!ucsContext.loaded) {
      return
    }

    setUserCompanySettings(ucsContext.ucs.active_ucs)
  }, [ucsContext])

  useEffect(() => {
    if (!checkFlag('paymentsV2')) {
      return
    }
    getPaymentRequests()
  }, [])

  useEffect(() => {
    // add approvable prs to state
    let newApprovable = filterApprovableRequests([...paymentRequests])
    setApprovableRequests(newApprovable)

    // checks if we have pulled all prs yet
    if (totalRecords >= 0 && paymentRequests.length == totalRecords) {
      setLoading(false)
    }
  }, [paymentRequests])

  return (
    <OuterBox>
      <Box sx={{ margin: theme.dashboard.margins }}>
        {!checkFlag('paymentsV2') ? (
          <ComingSoonTab config='approvals' title='Approvals' />
        ) : (
          <Stack spacing={1}>
            <HeaderBox
              title='Approvals'
              actions={
                <HideOnMobile>
                  <Space>
                    <FormControlLabel
                      labelPlacement='start'
                      label={
                        <Typography color='secondary' sx={{ fontStyle: 'italic' }}>
                          Requests I can approve
                        </Typography>
                      }
                      control={
                        <Switch
                          sx={{
                            '& .MuiSwitch-switchBase': {
                              color: theme.palette.secondary.light,
                            },
                            '& .Mui-checked': {
                              color: theme.palette.primary.main,
                            },
                          }}
                          defaultChecked={false}
                          onChange={() => {
                            toggleData()
                          }}
                        />
                      }
                    />
                    <BulkApprovalButton
                      tableData={paymentRequests}
                      selectedRowIds={selectedRowIds}
                      updateBulkPaymentRequests={updateBulkPaymentRequests}
                    />
                  </Space>
                </HideOnMobile>
              }
            />
            <PaymentsRequestsTable
              paymentRequests={displayAllData ? paymentRequests : approvableRequests}
              loading={loading}
              updatePaymentRequests={updatePaymentRequests}
              onRowSelect={onRowSelect}
            />
          </Stack>
        )}
      </Box>
    </OuterBox>
  )
}

export default Approvals
